// Attendance policy API (Phase 3) — on the existing `attendance` function.
// Managing policies is admin / superadmin; the server checks it too.
import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";
import type { ReportField } from "@/lib/policyFormula";

export type PolicySlot = { name: string; start?: string; end?: string };

export type PolicySettings = {
    schema?: number;
    subject: "select" | "none";
    slots: PolicySlot[];
    duplicateWarning: boolean;
    counting: "sessions" | "days";
    studentsSeeAttendance: boolean;   // false: students don't see attendance (counting carries on)
    minimumPercent: number | null;
    customColumns: ReportField[];   // one value per student (report formulas)
    headerFields: ReportField[];    // one value per report
};

export type Policy = {
    policyKey: string;
    name: string;
    basedOn: string | null;
    settings: PolicySettings;
    version: number;
    effectiveFrom: string;
};

export type Preset = { key: string; name: string; settings: PolicySettings };

export type Assignment = { level: "department" | "programme" | "classroom"; targetId: string; policyKey: string; enforced: boolean };

export type PolicyList = { presets: Preset[]; policies: Policy[]; assignments: Assignment[] };

// Errors carry the server's problem list (INVALID_POLICY) as `details`.
export class PolicyError extends Error {
    details: string[];
    constructor(message: string, details: string[] = []) { super(message); this.details = details; }
}

const MESSAGES: Record<string, string> = {
    POLICY_IN_USE: "This policy is still applied somewhere. Remove it there first.",
    VERSION_CONFLICT: "Someone saved this policy at the same time. Reload and try again.",
    POLICY_NOT_FOUND: "This policy no longer exists.",
    DEPARTMENT_NOT_FOUND: "This department no longer exists.",
    PROGRAMME_NOT_FOUND: "This programme no longer exists.",
    INVALID_NAME: "Give the policy a name (up to 80 characters).",
    INVALID_DATE: "Pick a valid start date.",
    INVALID_POLICY: "Some settings need fixing:",
};

async function call(path: string, body?: unknown) {
    let res: Response;
    try {
        res = await fetch(`${environment.BASE_URL}/attendance/${path}`, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify(body ?? {}),
        });
    } catch {
        throw new PolicyError("Could not reach the server. Check your connection.");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
        const code = data?.message || `Request failed (${res.status})`;
        throw new PolicyError(MESSAGES[ code ] || code, data?.details || []);
    }
    return data;
}

export const listPolicies = async (): Promise<PolicyList> => {
    const d = await call("listPolicies");
    return { presets: d.presets || [], policies: d.policies || [], assignments: d.assignments || [] };
};

// New policy (basedOn = preset key) or, with policyKey, a new version of it.
// The report lists may be left out of a save (the server fills defaults).
type SavedSettings = Omit<PolicySettings, "customColumns" | "headerFields"> & Partial<Pick<PolicySettings, "customColumns" | "headerFields">>;

export const savePolicy = async (body: { policyKey?: string; name: string; basedOn?: string; settings: SavedSettings; effectiveFrom?: string }): Promise<Policy> =>
    (await call("savePolicy", body)).policy;

export const archivePolicy = async (policyKey: string) => call("archivePolicy", { policyKey });

// A draft run over a department's real registers (nothing saved).
export type PreviewStudent = {
    eid: string; name: string | null; sessionsOn: number; sessionsPresent: number; daysOn: number;
    todayPercent: number | null; draftPercent: number | null; change: number | null; belowMinimum: boolean;
    columns: Record<string, number | null>;
};
export type PreviewResult = {
    from: string; to: string;
    summary: { registers: number; students: number; changed: number; largestChange: number; belowMinimum: number; minimumPercent: number | null; counting: string; studentsSeeAttendance: boolean };
    headers: Record<string, number | null>;
    students: PreviewStudent[];
    truncated: boolean;
};
export const previewPolicy = async (body: { settings: Partial<PolicySettings>; departmentName: string; programmeId?: string | null; days?: number }): Promise<PreviewResult> =>
    (await call("previewPolicy", body)) as PreviewResult;

export const assignPolicy = async (body: Assignment) => call("assignPolicy", body);

export const unassignPolicy = async (level: Assignment[ "level" ], targetId: string) => call("unassignPolicy", { level, targetId });
